/**
 * The tonal palette of one colour (ADR-060). From the member's seed colour it builds the whole
 * ground of the app in OKLCH: mostly neutrals that lean a little towards the seed, soft tints, and
 * the action colour itself. Status colours (success, warn, danger) are not here on purpose: they
 * keep their meaning whatever the member picks. Every text pair is pushed until it meets AA, so a
 * colour typed by hand cannot produce an unreadable app. Pure: no DOM, no storage.
 */
export type Scheme = 'light' | 'dark';

interface Oklch {
  l: number;
  c: number;
  h: number;
}

const WHITE = '#ffffff';
const AA = 4.5;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1, 7), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToOklch([r8, g8, b8]: [number, number, number]): Oklch {
  const [r, g, b] = [toLinear(r8 / 255), toLinear(g8 / 255), toLinear(b8 / 255)];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const b2 = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    c: Math.hypot(a, b2),
    h: Math.atan2(b2, a),
  };
}

/** Linear sRGB of a colour; values outside 0..1 mean the colour is out of gamut. */
function linearRgb({ l, c, h }: Oklch): [number, number, number] {
  const a = c * Math.cos(h);
  const b = c * Math.sin(h);
  const lp = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mp = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const sp = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * lp - 3.3077115913 * mp + 0.2309699292 * sp,
    -1.2684380046 * lp + 2.6097574011 * mp - 0.3413193965 * sp,
    -0.0041960863 * lp - 0.7034186147 * mp + 1.707614701 * sp,
  ];
}

/** To hex, giving up chroma (never lightness or hue) when the colour is out of gamut. */
function oklchToHex(color: Oklch): string {
  let c = color.c;
  let rgb = linearRgb({ ...color, c });
  while (c > 0 && rgb.some((v) => v < -0.0005 || v > 1.0005)) {
    c = Math.max(0, c - 0.002);
    rgb = linearRgb({ ...color, c });
  }
  const byte = (v: number) =>
    Math.round(clamp(toGamma(clamp(v, 0, 1)), 0, 1) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${rgb.map(byte).join('')}`;
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => toLinear(v / 255));
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return ((hi ?? 0) + 0.05) / ((lo ?? 0) + 0.05);
}

/** Moves `color` lighter or darker until it reads on every ground in `grounds`. */
function readable(color: Oklch, grounds: string[], step: number): Oklch {
  let next = color;
  while (next.l > 0 && next.l < 1 && grounds.some((g) => contrast(oklchToHex(next), g) < AA)) {
    next = { ...next, l: clamp(next.l + step, 0, 1) };
  }
  return next;
}

/**
 * The filled action colour and the text on it. A seed that carries white text stays exactly as
 * picked; a slightly bright one is darkened a little; a truly bright one keeps its colour and takes
 * dark text instead of turning brown.
 */
function solidOf(seed: Oklch): { solid: Oklch; on: string } {
  const lifted = { ...seed };
  const darkened = readable(lifted, [WHITE], -0.005);
  if (lifted.l - darkened.l <= 0.16) return { solid: darkened, on: WHITE };
  const ink = readable({ l: 0.22, c: Math.min(seed.c, 0.02), h: seed.h }, [oklchToHex(lifted)], -0.01);
  return { solid: lifted, on: oklchToHex(ink) };
}

/** `#rrggbbaa`: shadows are a colour with an alpha, so they lean to the theme without muddying it. */
const alpha = (hex: string, a: number) =>
  `${hex}${Math.round(a * 255)
    .toString(16)
    .padStart(2, '0')}`;

function normalise(seedHex: string): Oklch {
  return rgbToOklch(hexToRgb(seedHex));
}

export const HEX_COLOR = /^#[0-9a-f]{6}$/;

const DEG = Math.PI / 180;
const WARM_ANCHOR = 82 * DEG;
const COOL_ANCHOR = 240 * DEG;

/** The shortest turn from `to` to `from`, in radians. */
function turn(from: number, to: number): number {
  return Math.atan2(Math.sin(from - to), Math.cos(from - to));
}

/**
 * The seed's companion: the soft colour of what is selected or under the pointer, so these differ
 * from both the white and the deep colour (employer's reference, 2026-11-01: petrol with champagne).
 * Most colours pair with a warm sand; a warm seed pairs with a dusty slate. It leans a little
 * towards a seed close by, so most themes have a companion of their own; a seed across the wheel
 * from it would drag it to a muddy hue, so that one takes the anchor itself.
 */
function companionOf(seed: Oklch): Oklch {
  const warmSeed = seed.c > 0.03 && turn(seed.h, 65 * DEG) > -45 * DEG && turn(seed.h, 65 * DEG) < 45 * DEG;
  const anchor = warmSeed ? COOL_ANCHOR : WARM_ANCHOR;
  const away = turn(seed.h, anchor);
  const lean = Math.abs(away) > 120 * DEG ? 0 : clamp(away * 0.14, -14 * DEG, 14 * DEG);
  return { l: 0.76, c: 0.055, h: anchor + lean };
}

/** A pair given exactly, as drawn in a reference, where the engine would only come close. */
const EXACT: Record<string, { companion: string; soft: string; ground: string }> = {
  '#173f43': { companion: '#c7ae88', soft: '#f1e9dd', ground: '#faf9f6' },
};

/** Every token this module owns, as `--name: value`. index.css carries the same set for the default seed. */
export function buildTones(seedHex: string, scheme: Scheme): Record<string, string> {
  const seed = normalise(seedHex);
  const { c: seedC, h } = seed;
  const light = scheme === 'light';
  const exact = light ? EXACT[seedHex.toLowerCase()] : undefined;
  const companion = exact ? normalise(exact.companion) : companionOf(seed);

  // The neutrals: about four parts in five of the screen, a breath of the companion's hue. The
  // ground reads as white at first sight (employer, 2026-11-01); what sits on it carries the colour.
  // At night a warm companion would turn the ground brown, so it leans to the seed instead.
  const lean = light ? 0.005 : 0.006;
  const neutral = (l: number, k = 1) => oklchToHex({ l, c: lean * k, h: light ? companion.h : h });
  const ground = light
    ? { bg: 0.98, tint: 0.962, surface: 0.995, s2: 0.968, s3: 0.948, raised: 0.998, sunken: 0.958 }
    : { bg: 0.245, tint: 0.215, surface: 0.27, s2: 0.3, s3: 0.325, raised: 0.295, sunken: 0.22 };
  const bg = exact?.ground ?? neutral(ground.bg);
  const surface2 = neutral(ground.s2, light ? 3 : 2);
  const surface3 = neutral(ground.s3, light ? 3.6 : 2.4);
  const sunken = neutral(ground.sunken, light ? 2 : 1);

  // The soft side of the theme: selected things, the moving pill, a row under the pointer.
  const soft = exact?.soft ?? oklchToHex(light ? { l: 0.935, c: 0.02, h: companion.h } : { l: 0.34, c: 0.03, h: companion.h });
  const line = oklchToHex(light ? { l: 0.84, c: 0.05, h: companion.h } : { l: 0.46, c: 0.045, h: companion.h });

  // Text leans to the seed, as a heading in the reference does; it stays text first.
  const inkStep = light ? -0.005 : 0.005;
  const grounds = [bg, surface2, surface3, sunken, soft];
  const inkLights = light ? [0.27, 0.38, 0.45, 0.5] : [0.95, 0.86, 0.79, 0.75];
  const inkChromas = light ? [0.4, 0.3, 0.22, 0.18] : [0.1, 0.08, 0.06, 0.06];
  const text = (i: number) =>
    oklchToHex(readable({ l: inkLights[i] ?? 0, c: Math.min(seedC * (inkChromas[i] ?? 0), 0.05), h }, grounds, inkStep));

  // The action colour: filled, as text, and its hover fill.
  const base = light ? seed : { ...seed, l: Math.max(seed.l, 0.45) };
  const { solid, on } = solidOf(base);
  const solidHex = oklchToHex(solid);
  const accentStart = light ? seed : { l: 0.8, c: Math.min(seedC, 0.12), h };
  const accent = oklchToHex(readable(accentStart, [neutral(ground.surface), ...grounds], inkStep));
  const hover = oklchToHex({ ...solid, l: solid.l + (on === WHITE ? -0.06 : 0.04) });

  // The deep side: the rail and the phone's dock, always dark enough to carry light text.
  const deep = light
    ? { l: clamp(seed.l, 0.27, 0.36), c: Math.min(seedC, 0.09), h }
    : { l: 0.2, c: Math.min(seedC, 0.05), h };
  const deepHex = oklchToHex(deep);
  const activeFill = light ? companion : { ...companion, l: 0.68 };
  const activeHex = oklchToHex(activeFill);
  const activeInk = oklchToHex(readable({ l: 0.27, c: Math.min(seedC, 0.06), h }, [activeHex], -0.005));
  const toneInk = oklchToHex(readable(light ? { l: 0.3, c: Math.min(seedC, 0.07), h } : { l: 0.9, c: 0.03, h }, [soft], inkStep));
  const railInk = oklchToHex({ l: 0.97, c: 0.008, h: companion.h });
  const railQuiet = oklchToHex(readable({ l: 0.84, c: 0.015, h: companion.h }, [deepHex], 0.005));

  const shadowDark = light ? alpha(oklchToHex({ l: 0.62, c: Math.min(seedC * 0.12, 0.018), h }), 0.3) : alpha('#000000', 0.42);
  const shadowLight = light ? alpha(WHITE, 0.9) : alpha(WHITE, 0.045);

  return {
    '--bg': bg,
    '--bg-tint': neutral(ground.tint, light ? 1.6 : 1),
    '--surface': neutral(ground.surface, light ? 0.4 : 1),
    '--surface-2': surface2,
    '--surface-3': surface3,
    '--surface-raised': neutral(ground.raised, 0.3),
    '--surface-sunken': sunken,
    '--border': neutral(light ? 0.905 : 0.36, 3),
    '--border-strong': neutral(light ? 0.82 : 0.44, 2),
    '--hairline': neutral(light ? 0.935 : 0.31, 3),
    '--text': text(0),
    '--text-2': text(1),
    '--text-3': text(2),
    '--text-4': text(3),
    '--accent': accent,
    '--accent-600': hover,
    '--accent-soft': soft,
    '--accent-line': line,
    '--accent-solid': solidHex,
    '--on-accent': on,
    '--tone': oklchToHex(companion),
    '--tone-ink': toneInk,
    '--sidebar': deepHex,
    '--sidebar-ink': railInk,
    '--sidebar-quiet': railQuiet,
    '--sidebar-active': activeHex,
    '--sidebar-active-ink': activeInk,
    '--shadow-dark': shadowDark,
    '--shadow-light': shadowLight,
  };
}
